import * as React from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import {
  ApiClient,
  ApiClientError,
  ObsoleteRequest,
  type Page,
  type SessionProfile,
} from "./api-client";
import { Button } from "./Button";
import { coreTokens } from "./theme";
import { inventoryCsv, type InventoryCsvRow } from "./inventory-csv";

type Module =
  "home" | "users" | "roles" | "organizations" | "branches" | "configuration" | "inventory";
interface Entity {
  _id: string;
  name?: string;
  fullName?: string;
  email?: string;
  active?: boolean;
  protected?: boolean;
  roleIds?: string[];
  permissions?: string[];
  displayName?: string;
  enabledModules?: string[];
  availableModules?: string[];
  action?: string;
  resource?: string;
  createdAt?: string;
  sku?: string;
  category?: string;
  stock?: number;
  price?: number;
}
const modules: { key: Module; label: string; icon: string; description: string }[] = [
  { key: "home", label: "Inicio", icon: "⌂", description: "Tu espacio de trabajo" },
  { key: "users", label: "Usuarios", icon: "♙", description: "Personas y accesos a tu empresa" },
  {
    key: "roles",
    label: "Roles y permisos",
    icon: "◇",
    description: "Define quién puede realizar cada acción",
  },
  {
    key: "organizations",
    label: "Organización",
    icon: "▦",
    description: "La identidad de tu empresa",
  },
  {
    key: "branches",
    label: "Sucursales",
    icon: "⌖",
    description: "Organiza tus lugares de trabajo",
  },
  {
    key: "inventory",
    label: "Inventario",
    icon: "▣",
    description: "Productos, existencias y precios",
  },
  {
    key: "configuration",
    label: "Configuración",
    icon: "⚙",
    description: "Adapta el CORE a tu operación",
  },
];
const permissionCatalogue = [
  "users.read",
  "users.create",
  "users.update",
  "users.assignRoles",
  "roles.read",
  "roles.create",
  "roles.update",
  "organizations.read",
  "organizations.create",
  "organizations.update",
  "branches.read",
  "branches.create",
  "branches.update",
  "configuration.read",
  "configuration.update",
  "audit.read",
  "inventory.read",
  "inventory.create",
  "inventory.update",
  "inventory.export",
];
const moduleNames: Record<string, string> = Object.fromEntries(
  modules.map((module) => [module.key, module.label]),
);
moduleNames.audit = "Auditoría";
const emptyForm = {
  name: "",
  email: "",
  password: "",
  active: true,
  selected: [] as string[],
  sku: "",
  category: "",
  stock: "0",
  price: "0.00",
};

function Field({
  label,
  value,
  onChange,
  secret = false,
  multiline = false,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  secret?: boolean;
  multiline?: boolean;
  error?: string;
}) {
  const errorId = React.useId();
  return (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <TextInput
        accessibilityLabel={label}
        accessibilityHint={error}
        {...(Platform.OS === "web"
          ? { "aria-invalid": Boolean(error), "aria-describedby": error ? errorId : undefined }
          : {})}
        value={value}
        onChangeText={onChange}
        secureTextEntry={secret}
        autoCapitalize="none"
        multiline={multiline}
        style={s.input}
      />
      {error ? (
        <Text nativeID={errorId} accessibilityRole="alert" style={s.errorText}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}
function Toggle({
  label,
  selected,
  onPress,
  disabled = false,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected, disabled }}
      accessibilityLabel={label}
      onPress={onPress}
      disabled={disabled}
      style={[s.toggle, selected && s.toggleSelected, disabled && { opacity: 0.5 }]}
    >
      <Text style={s.body}>
        {selected ? "☑" : "☐"} {label}
      </Text>
    </Pressable>
  );
}

export function CoreApp({
  client,
  resetToken,
  downloadInventoryCsv,
}: {
  client: ApiClient;
  resetToken?: string;
  downloadInventoryCsv?: (csv: string) => void;
}): React.JSX.Element {
  const { width } = useWindowDimensions();
  const compact = width < 800;
  const [profile, setProfile] = React.useState<SessionProfile | null>(null);
  const [booting, setBooting] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [switching, setSwitching] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [authView, setAuthView] = React.useState<"login" | "forgot" | "reset">(
    resetToken ? "reset" : "login",
  );
  const [resetPassword, setResetPassword] = React.useState("");
  const [resetConfirmation, setResetConfirmation] = React.useState("");
  const [module, setModule] = React.useState<Module>("home");
  const [navigationSearch, setNavigationSearch] = React.useState("");
  const [drawer, setDrawer] = React.useState(false);
  const [userMenu, setUserMenu] = React.useState(false);
  const [companyMenu, setCompanyMenu] = React.useState(false);
  const [items, setItems] = React.useState<Entity[]>([]);
  const [roles, setRoles] = React.useState<Entity[]>([]);
  const [counts, setCounts] = React.useState<Record<string, number>>({});
  const [activity, setActivity] = React.useState<Entity[]>([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [query, setQuery] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState<Entity | "new" | null>(null);
  const [form, setForm] = React.useState(emptyForm);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [refreshKey, setRefreshKey] = React.useState(0);
  const sequence = React.useRef(0);
  const submitting = React.useRef(false);
  const can = (permission: string) => profile?.permissions.includes(permission) ?? false;
  const clearData = React.useCallback(() => {
    sequence.current += 1;
    setItems([]);
    setTotal(0);
    setTotalPages(1);
    setLoadFailed(false);
    setRoles([]);
    setCounts({});
    setActivity([]);
    setEditing(null);
    setFieldErrors({});
    setPage(1);
    setQuery("");
    setSearch("");
    setNotice("");
    setError("");
  }, []);
  React.useEffect(() => {
    let mounted = true;
    client.onExpired = () => {
      if (mounted) {
        clearData();
        setProfile(null);
        setError("Tu sesión expiró. Inicia sesión de nuevo.");
      }
    };
    client
      .restore()
      .then((value) => {
        if (mounted) setProfile(value);
      })
      .catch((failure: unknown) => {
        if (mounted && failure instanceof ApiClientError && failure.status !== 401)
          setError(failure.message);
      })
      .finally(() => {
        if (mounted) setBooting(false);
      });
    return () => {
      mounted = false;
      client.onExpired = () => undefined;
    };
  }, [client, clearData]);

  React.useEffect(() => {
    const current = ++sequence.current;
    setItems([]);
    setRoles([]);
    setActivity([]);
    setCounts({});
    if (!profile?.tenantId) return;
    setLoading(true);
    setLoadFailed(false);
    setTotal(0);
    setTotalPages(1);
    setError("");
    const load = async () => {
      if (module === "home") {
        const visible = ["users", "branches", "roles"].filter((key) =>
          profile.permissions.includes(`${key}.read`),
        );
        const results = await Promise.all(
          visible.map(async (key) => {
            try {
              const result = await client.request<Page<Entity>>(`/${key}?limit=1`);
              return [key, result.pagination.total] as const;
            } catch (failure) {
              if (failure instanceof ApiClientError && failure.status === 403) return null;
              throw failure;
            }
          }),
        );
        if (current !== sequence.current) return;
        setCounts(Object.fromEntries(results.filter((value) => value !== null)));
        if (profile.permissions.includes("audit.read")) {
          try {
            const recent = await client.request<Page<Entity>>("/audit?limit=5");
            if (current === sequence.current) setActivity(recent.data);
          } catch (failure) {
            if (!(failure instanceof ApiClientError && failure.status === 403)) throw failure;
          }
        }
      } else {
        const result = await client.request<Page<Entity>>(
          `/${module}?page=${page}&limit=10&q=${encodeURIComponent(query)}`,
        );
        if (current !== sequence.current) return;
        setItems(result.data);
        setTotal(result.pagination.total);
        setTotalPages(result.pagination.totalPages);
        if (module === "users" && profile.permissions.includes("roles.read")) {
          const available: Entity[] = [];
          let rolePage = 1;
          let lastPage = 1;
          do {
            const roleResult = await client.request<Page<Entity>>(
              `/roles?page=${rolePage}&limit=100`,
            );
            available.push(...roleResult.data);
            lastPage = roleResult.pagination.totalPages;
            rolePage += 1;
          } while (rolePage <= lastPage && current === sequence.current);
          if (current === sequence.current) setRoles(available);
        }
      }
    };
    void load()
      .catch((failure: unknown) => {
        if (current === sequence.current && !(failure instanceof ObsoleteRequest)) {
          setLoadFailed(true);
          setError(failure instanceof Error ? failure.message : "No se pudieron cargar los datos");
        }
      })
      .finally(() => {
        if (current === sequence.current) setLoading(false);
      });
    return () => {
      sequence.current += 1;
    };
  }, [client, profile, module, page, query, refreshKey]);

  const run = async (action: () => Promise<void>) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
    } catch (failure) {
      if (!(failure instanceof ObsoleteRequest))
        setError(failure instanceof Error ? failure.message : "No se pudo completar la operación");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  const navigate = (target: Module) => {
    clearData();
    setModule(target);
    setDrawer(false);
    setNavigationSearch("");
  };
  const chooseCompany = (id: string) =>
    void run(async () => {
      clearData();
      setSwitching(true);
      setCompanyMenu(false);
      setModule("home");
      try {
        setProfile(await client.switchOrganization(id));
      } catch (failure) {
        setProfile(await client.request<SessionProfile>("/auth/me"));
        throw failure;
      } finally {
        setSwitching(false);
      }
    });
  const edit = (item: Entity | "new") => {
    setEditing(item);
    setFieldErrors({});
    setError("");
    setNotice("");
    setForm(
      item === "new"
        ? { ...emptyForm, selected: [] }
        : {
            name: item.name ?? item.fullName ?? item.displayName ?? "",
            email: item.email ?? "",
            password: "",
            active: item.active ?? true,
            selected: item.roleIds ?? item.permissions ?? item.enabledModules ?? [],
            sku: item.sku ?? "",
            category: item.category ?? "",
            stock: String(item.stock ?? 0),
            price: String(item.price ?? 0),
          },
    );
  };
  const toggle = (value: string) =>
    setForm((old) => ({
      ...old,
      selected: old.selected.includes(value)
        ? old.selected.filter((entry) => entry !== value)
        : [...old.selected, value],
    }));
  const save = () =>
    void run(async () => {
      if (!editing) return;
      const updating = editing !== "new";
      const invalid: Record<string, string> = {};
      if (!form.name.trim() && (module !== "users" || !updating))
        invalid.name = "El nombre es obligatorio";
      if (form.name.length > 120) invalid.name = "El nombre admite hasta 120 caracteres";
      if (module === "users" && !updating) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
          invalid.email = "Introduce un correo válido";
        if (form.password && (form.password.length < 12 || form.password.length > 128))
          invalid.password = "La contraseña debe tener entre 12 y 128 caracteres";
      }
      if (module === "users" && !form.selected.length) invalid.roles = "Selecciona al menos un rol";
      if (module === "inventory") {
        if (!form.sku.trim()) invalid.sku = "El SKU es obligatorio";
        if (!form.category.trim()) invalid.category = "La categoría es obligatoria";
        if (!/^\d+$/.test(form.stock) || Number(form.stock) > 2_147_483_647)
          invalid.stock = "El stock debe ser un entero mayor o igual a cero";
        if (!/^\d+(\.\d{1,2})?$/.test(form.price) || Number(form.price) > 999_999_999_999)
          invalid.price = "El precio debe ser mayor o igual a cero, con hasta dos decimales";
      }
      setFieldErrors(invalid);
      if (Object.keys(invalid).length) return;
      let body: unknown;
      if (module === "users") {
        if (updating) {
          let rolesSaved = false;
          try {
            if (
              can("users.assignRoles") &&
              JSON.stringify(form.selected) !== JSON.stringify(editing.roleIds ?? [])
            ) {
              await client.request(`/users/${editing._id}/roles`, "PUT", {
                roleIds: form.selected,
              });
              rolesSaved = true;
            }
            if (can("users.update") && form.active !== editing.active)
              await client.request(`/users/${editing._id}`, "PATCH", { active: form.active });
          } catch (failure) {
            if (rolesSaved && !(failure instanceof ObsoleteRequest)) {
              setEditing(null);
              setRefreshKey((value) => value + 1);
              throw new Error(
                `Los roles se guardaron, pero no se pudo cambiar el estado. ${failure instanceof Error ? failure.message : "Vuelve a abrir el usuario para intentarlo."}`,
              );
            }
            throw failure;
          }
          setEditing(null);
          setNotice("Usuario actualizado");
          setRefreshKey((value) => value + 1);
          return;
        }
        body = {
          email: form.email,
          fullName: form.name,
          roleIds: form.selected,
          ...(form.password ? { password: form.password } : {}),
        };
      } else if (module === "roles") body = { name: form.name, permissions: form.selected };
      else if (module === "inventory")
        body = {
          sku: form.sku,
          name: form.name,
          category: form.category,
          stock: Number(form.stock),
          price: Number(form.price),
          active: form.active,
        };
      else if (module === "configuration")
        body = { displayName: form.name, enabledModules: form.selected };
      else if (module === "branches") body = { name: form.name, active: form.active };
      else body = { name: form.name };
      const path = `/${module}${updating && module !== "configuration" ? `/${editing._id}` : ""}`;
      await client.request(path, updating || module === "configuration" ? "PATCH" : "POST", body);
      setEditing(null);
      setNotice("Cambios guardados correctamente");
      setRefreshKey((value) => value + 1);
      if (module === "organizations") setProfile(await client.request<SessionProfile>("/auth/me"));
    });
  const exportInventory = () =>
    void run(async () => {
      const rows = await client.request<InventoryCsvRow[]>("/inventory/export");
      if (!downloadInventoryCsv) return;
      downloadInventoryCsv(inventoryCsv(rows));
      setNotice("Inventario exportado correctamente");
    });
  const currentModule = modules.find((entry) => entry.key === module)!;
  const company = profile?.organizations.find((entry) => entry.id === profile.tenantId);
  const visibleModules = modules.filter(
    (entry) =>
      (entry.key === "home" || can(`${entry.key}.read`)) &&
      entry.label.toLowerCase().includes(navigationSearch.toLowerCase()),
  );
  const status = (
    <>
      {error ? (
        <View accessibilityRole="alert" style={s.error}>
          <Text style={s.errorText}>{error}</Text>
        </View>
      ) : null}
      {notice ? (
        <View accessibilityRole="alert" style={s.success}>
          <Text style={s.body}>{notice}</Text>
        </View>
      ) : null}
    </>
  );

  if (booting || switching)
    return (
      <View style={s.loginOuter}>
        <ActivityIndicator
          accessibilityLabel="Restaurando sesión"
          color={coreTokens.colors.primary}
        />
        <Text style={s.muted}>
          {switching ? "Cambiando de empresa…" : "Preparando tu espacio…"}
        </Text>
      </View>
    );
  if (!profile)
    return (
      <ScrollView contentContainerStyle={s.loginOuter}>
        <View style={s.loginCard}>
          <Text style={s.brand}>▦ ERP · CORE</Text>
          <Text style={s.eyebrow}>TU EMPRESA, EN UN SOLO LUGAR</Text>
          <Text style={s.loginTitle}>
            {authView === "login" ? (
              <>Bienvenido a tu{"\n"}espacio de trabajo.</>
            ) : authView === "forgot" ? (
              "Recupera tu contraseña"
            ) : (
              "Define una nueva contraseña"
            )}
          </Text>
          <Text style={s.muted}>
            {authView === "login"
              ? "Gestiona tu equipo, sus accesos y la estructura de tu organización."
              : authView === "forgot"
                ? "Escribe tu correo. La respuesta será la misma exista o no una cuenta."
                : "Elige una contraseña de al menos 12 caracteres."}
          </Text>
          {status}
          {authView === "login" ? (
            <>
              <Field label="Correo electrónico" value={email} onChange={setEmail} />
              <Field label="Contraseña" value={password} onChange={setPassword} secret />
              <Button
                label={busy ? "Conectando…" : "Iniciar sesión →"}
                disabled={busy || !email || !password}
                onPress={() =>
                  void run(async () => {
                    setProfile(await client.login(email, password));
                    setPassword("");
                  })
                }
              />
              <Button
                label="¿Olvidaste tu contraseña?"
                variant="secondary"
                disabled={busy}
                onPress={() => {
                  setAuthView("forgot");
                  setError("");
                  setNotice("");
                }}
              />
            </>
          ) : null}
          {authView === "forgot" ? (
            <>
              <Field label="Correo electrónico" value={email} onChange={setEmail} />
              <Button
                label={busy ? "Enviando…" : "Enviar instrucciones"}
                disabled={busy || !email}
                onPress={() => void run(async () => setNotice(await client.forgotPassword(email)))}
              />
              <Button
                label="Volver al inicio de sesión"
                variant="secondary"
                disabled={busy}
                onPress={() => {
                  setAuthView("login");
                  setError("");
                }}
              />
              {Platform.OS !== "web" ? (
                <Text style={s.small}>
                  El enlace abre el flujo web seguro. Los enlaces universales de la aplicación móvil
                  aún no están configurados.
                </Text>
              ) : null}
            </>
          ) : null}
          {authView === "reset" ? (
            <>
              <Field
                label="Nueva contraseña"
                value={resetPassword}
                onChange={setResetPassword}
                secret
              />
              <Field
                label="Confirmar nueva contraseña"
                value={resetConfirmation}
                onChange={setResetConfirmation}
                secret
                error={
                  resetConfirmation && resetPassword !== resetConfirmation
                    ? "Las contraseñas no coinciden"
                    : undefined
                }
              />
              <Button
                label={busy ? "Actualizando…" : "Actualizar contraseña"}
                disabled={
                  busy ||
                  !resetToken ||
                  resetPassword.length < 12 ||
                  resetPassword !== resetConfirmation
                }
                onPress={() =>
                  void run(async () => {
                    setNotice(await client.resetPassword(resetToken ?? "", resetPassword));
                    setResetPassword("");
                    setResetConfirmation("");
                    setAuthView("login");
                  })
                }
              />
              <Button
                label="Volver al inicio de sesión"
                variant="secondary"
                disabled={busy}
                onPress={() => setAuthView("login")}
              />
            </>
          ) : null}
          <Text style={s.small}>
            Acceso exclusivo para usuarios autorizados de tu organización.
          </Text>
        </View>
      </ScrollView>
    );
  if (!profile.tenantId || companyMenu)
    return (
      <ScrollView contentContainerStyle={s.loginOuter}>
        <View style={s.loginCard}>
          <Text style={s.brand}>▦ ERP · CORE</Text>
          <Text style={s.title}>Selecciona tu empresa</Text>
          <Text style={s.muted}>
            {profile.user.fullName}, elige el espacio en el que quieres trabajar.
          </Text>
          {status}
          {profile.organizations.length ? (
            profile.organizations.map((org) => (
              <Button
                key={org.id}
                label={org.name}
                variant="secondary"
                disabled={busy}
                onPress={() => chooseCompany(org.id)}
              />
            ))
          ) : (
            <Text style={s.body}>No tienes membresías activas. Contacta con el administrador.</Text>
          )}
          {profile.tenantId ? (
            <Button
              label="Volver"
              variant="secondary"
              onPress={() => setCompanyMenu(false)}
              disabled={busy}
            />
          ) : null}
          <Button
            label="Cerrar sesión"
            variant="secondary"
            disabled={busy}
            onPress={() =>
              void run(async () => {
                await client.logout();
                clearData();
                setProfile(null);
              })
            }
          />
        </View>
      </ScrollView>
    );

  return (
    <View style={[s.outer, compact && { padding: 8 }]}>
      <View style={s.shell}>
        <View style={[s.header, compact && { padding: 16 }]}>
          <Text style={s.brand}>
            ▦ ERP <Text style={s.brandLight}>/ CORE</Text>
          </Text>
          <View style={{ flex: 1 }} />
          {compact ? (
            <Button label="☰ Menú" variant="secondary" onPress={() => setDrawer(!drawer)} />
          ) : (
            <View style={{ width: 245 }}>
              <TextInput
                style={s.searchInput}
                accessibilityLabel="Buscar módulo de navegación"
                placeholder="⌕  Buscar un módulo…"
                value={navigationSearch}
                onChangeText={setNavigationSearch}
              />
            </View>
          )}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Cambiar empresa"
            onPress={() => setCompanyMenu(true)}
            disabled={busy}
            style={s.companyButton}
          >
            <Text style={s.label}>{company?.name ?? "Empresa"} ▾</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Menú de usuario"
            onPress={() => setUserMenu(!userMenu)}
            style={s.avatar}
          >
            <Text style={s.avatarText}>{profile.user.fullName.slice(0, 1).toUpperCase()}</Text>
          </Pressable>
        </View>
        {userMenu ? (
          <View style={s.userMenu}>
            <Text style={s.label}>{profile.user.fullName}</Text>
            <Text style={s.muted}>{profile.user.email}</Text>
            <Button
              label="Cerrar sesión"
              variant="secondary"
              disabled={busy}
              onPress={() =>
                void run(async () => {
                  await client.logout();
                  clearData();
                  setProfile(null);
                  setUserMenu(false);
                })
              }
            />
          </View>
        ) : null}
        <View style={[s.layout, compact && { flexDirection: "column" }]}>
          {!compact || drawer ? (
            <View style={[s.nav, compact && { width: "100%", padding: 12 }]}>
              <Text style={s.navHeading}>ESPACIO DE TRABAJO</Text>
              {visibleModules.map((entry) => (
                <Pressable
                  key={entry.key}
                  accessibilityRole="button"
                  accessibilityState={{ selected: entry.key === module }}
                  accessibilityLabel={entry.label}
                  onPress={() => navigate(entry.key)}
                  style={[s.navItem, entry.key === module && s.navActive]}
                >
                  <Text style={[s.navIcon, entry.key === module && s.blue]}>{entry.icon}</Text>
                  <Text style={[s.navLabel, entry.key === module && s.blue]}>{entry.label}</Text>
                </Pressable>
              ))}
              {!visibleModules.length ? (
                <Text style={s.muted}>No hay módulos que coincidan.</Text>
              ) : null}
              <View style={s.navFooter}>
                <Text style={s.label}>Un espacio conectado</Text>
                <Text style={s.small}>Identidad, equipo y estructura empresarial.</Text>
                <View style={s.badge}>
                  <Text style={s.badgeText}>ERP CORE · Fase 2</Text>
                </View>
              </View>
            </View>
          ) : null}
          <ScrollView
            style={s.main}
            contentContainerStyle={[s.mainContent, compact && { padding: 16 }]}
            keyboardShouldPersistTaps="handled"
          >
            <View style={s.breadcrumb}>
              <Text style={s.small}>
                {company?.name} / {currentModule.label}
              </Text>
              <Text style={s.small}>● Sesión activa</Text>
            </View>
            <View style={s.titleRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.title}>
                  {module === "home"
                    ? `Hola, ${profile.user.fullName.split(" ")[0]}`
                    : currentModule.label}
                </Text>
                <Text style={s.muted}>{currentModule.description}</Text>
              </View>
              {module !== "home" &&
              module !== "configuration" &&
              can(`${module}.create`) &&
              (module !== "users" || (can("users.assignRoles") && can("roles.read"))) ? (
                <Button
                  label="＋ Agregar"
                  onPress={() => edit("new")}
                  disabled={busy || loading || loadFailed}
                />
              ) : null}
              {module === "inventory" && downloadInventoryCsv && can("inventory.export") ? (
                <Button
                  label="Exportar inventario"
                  variant="secondary"
                  onPress={exportInventory}
                  disabled={busy || loading || loadFailed}
                />
              ) : null}
            </View>
            {status}
            {loading ? (
              <View style={s.loading}>
                <ActivityIndicator color={coreTokens.colors.primary} />
                <Text style={s.muted}>Cargando información…</Text>
              </View>
            ) : null}
            {module === "home" ? (
              <>
                <View style={s.welcome}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.eyebrow}>TODO LO ESENCIAL, CONECTADO</Text>
                    <Text style={s.welcomeTitle}>Una base organizada{"\n"}para crecer.</Text>
                    <Text style={s.body}>
                      Administra tu equipo y construye la estructura de {company?.name}.
                    </Text>
                  </View>
                  <Text style={s.welcomeIcon}>▦</Text>
                </View>
                <View style={s.stats}>
                  {Object.entries(counts).map(([key, count], index) => (
                    <Pressable
                      key={key}
                      accessibilityRole="button"
                      accessibilityLabel={`Abrir ${moduleNames[key]}: ${count}`}
                      onPress={() => navigate(key as Module)}
                      style={[s.statCard, compact && { minWidth: 130 }]}
                    >
                      <View
                        style={[
                          s.statIcon,
                          { backgroundColor: ["#E5F2FF", "#F2EAFE", "#FFF3D5"][index] },
                        ]}
                      >
                        <Text style={s.navIcon}>
                          {modules.find((entry) => entry.key === key)?.icon}
                        </Text>
                      </View>
                      <Text style={s.statValue}>{count}</Text>
                      <Text style={s.muted}>{moduleNames[key]}</Text>
                      <Text style={s.statLink}>Ver detalles ↗</Text>
                    </Pressable>
                  ))}
                </View>
                <View style={s.panel}>
                  <Text style={s.sectionTitle}>Accesos rápidos</Text>
                  <View style={s.quickLinks}>
                    {modules
                      .filter((entry) => entry.key !== "home" && can(`${entry.key}.read`))
                      .map((entry) => (
                        <Button
                          key={entry.key}
                          label={`${entry.icon} ${entry.label}`}
                          variant="secondary"
                          onPress={() => navigate(entry.key)}
                        />
                      ))}
                  </View>
                </View>
                {can("audit.read") ? (
                  <View style={s.panel}>
                    <Text style={s.sectionTitle}>Actividad reciente</Text>
                    {activity.length ? (
                      activity.map((entry) => (
                        <View key={entry._id} style={s.activityRow}>
                          <Text style={s.activityDot}>●</Text>
                          <View style={{ flex: 1 }}>
                            <Text style={s.label}>
                              {entry.action} · {entry.resource}
                            </Text>
                            <Text style={s.small}>
                              {entry.createdAt
                                ? new Date(entry.createdAt).toLocaleString("es-MX")
                                : ""}
                            </Text>
                          </View>
                        </View>
                      ))
                    ) : (
                      <Text style={s.muted}>Sin actividad disponible para mostrar.</Text>
                    )}
                  </View>
                ) : null}
              </>
            ) : (
              <>
                {editing ? (
                  <View style={s.panel}>
                    <Text style={s.sectionTitle}>
                      {editing === "new" ? "Nuevo registro" : "Editar registro"}
                    </Text>
                    {module === "users" && editing !== "new" ? (
                      <>
                        <Text style={s.label}>{editing.fullName}</Text>
                        <Text style={s.muted}>{editing.email}</Text>
                        <Text style={s.small}>
                          Los cambios afectan a su membresía en esta empresa.
                        </Text>
                      </>
                    ) : (
                      <Field
                        label={module === "configuration" ? "Nombre visible" : "Nombre"}
                        value={form.name}
                        error={fieldErrors.name}
                        onChange={(name) => setForm({ ...form, name })}
                      />
                    )}
                    {module === "users" && editing === "new" ? (
                      <>
                        <Field
                          label="Correo del usuario"
                          value={form.email}
                          error={fieldErrors.email}
                          onChange={(value) => setForm({ ...form, email: value })}
                        />
                        <Field
                          label="Contraseña inicial (solo identidades nuevas)"
                          value={form.password}
                          error={fieldErrors.password}
                          onChange={(value) => setForm({ ...form, password: value })}
                          secret
                        />
                        <Text style={s.small}>
                          Mínimo 12 caracteres. Para incorporar una identidad existente, deja la
                          contraseña vacía.
                        </Text>
                      </>
                    ) : null}
                    {module === "inventory" ? (
                      <>
                        <Field
                          label="SKU"
                          value={form.sku}
                          error={fieldErrors.sku}
                          onChange={(sku) => setForm({ ...form, sku })}
                        />
                        <Field
                          label="Categoría"
                          value={form.category}
                          error={fieldErrors.category}
                          onChange={(category) => setForm({ ...form, category })}
                        />
                        <Field
                          label="Stock"
                          value={form.stock}
                          error={fieldErrors.stock}
                          onChange={(stock) => setForm({ ...form, stock })}
                        />
                        <Field
                          label="Precio"
                          value={form.price}
                          error={fieldErrors.price}
                          onChange={(price) => setForm({ ...form, price })}
                        />
                      </>
                    ) : null}
                    {module === "branches" ||
                    module === "inventory" ||
                    (module === "users" && editing !== "new") ? (
                      <Toggle
                        label={
                          module === "inventory" ? "Producto activo" : "Activo en esta empresa"
                        }
                        selected={form.active}
                        disabled={busy || (module === "users" && !can("users.update"))}
                        onPress={() => setForm({ ...form, active: !form.active })}
                      />
                    ) : null}
                    {module === "users" ? (
                      <>
                        <Text style={s.label}>Roles de la empresa</Text>
                        {fieldErrors.roles ? (
                          <Text accessibilityRole="alert" style={s.errorText}>
                            {fieldErrors.roles}
                          </Text>
                        ) : null}
                        {roles.length ? (
                          roles.map((role) => (
                            <Toggle
                              key={role._id}
                              label={role.name ?? "Rol"}
                              selected={form.selected.includes(role._id)}
                              disabled={busy || !can("users.assignRoles")}
                              onPress={() => toggle(role._id)}
                            />
                          ))
                        ) : (
                          <Text style={s.muted}>
                            No hay roles disponibles con tus permisos actuales.
                          </Text>
                        )}
                      </>
                    ) : null}
                    {module === "roles" ? (
                      <>
                        <Text style={s.label}>Permisos por acción</Text>
                        {permissionCatalogue.filter(can).map((value) => (
                          <Toggle
                            key={value}
                            label={value}
                            selected={form.selected.includes(value)}
                            disabled={busy}
                            onPress={() => toggle(value)}
                          />
                        ))}
                      </>
                    ) : null}
                    {module === "configuration" ? (
                      <>
                        <Text style={s.label}>Módulos disponibles para esta empresa</Text>
                        {(editing !== "new" ? (editing.availableModules ?? []) : []).map(
                          (value) => (
                            <Toggle
                              key={value}
                              label={moduleNames[value] ?? value}
                              selected={form.selected.includes(value)}
                              disabled={busy}
                              onPress={() => toggle(value)}
                            />
                          ),
                        )}
                        <Text style={s.small}>
                          Los permisos se aplican además de la habilitación de cada módulo.
                          Configuración permanece accesible para restaurarlos.
                        </Text>
                      </>
                    ) : null}
                    <View style={s.actions}>
                      <Button
                        label={busy ? "Guardando…" : "Guardar cambios"}
                        onPress={save}
                        disabled={busy}
                      />
                      <Button
                        label="Cancelar"
                        variant="secondary"
                        onPress={() => setEditing(null)}
                        disabled={busy}
                      />
                    </View>
                  </View>
                ) : null}
                <View style={s.panel}>
                  {!["configuration", "organizations"].includes(module) ? (
                    <View style={s.searchRow}>
                      <View style={{ flex: 1 }}>
                        <TextInput
                          accessibilityLabel={
                            module === "inventory"
                              ? "Buscar inventario por SKU, producto o categoría"
                              : "Buscar en el listado por nombre o correo"
                          }
                          placeholder={
                            module === "inventory"
                              ? "Buscar por SKU, producto o categoría…"
                              : "Buscar por nombre…"
                          }
                          style={s.input}
                          value={search}
                          onChangeText={setSearch}
                          onSubmitEditing={() => {
                            setPage(1);
                            setQuery(search);
                          }}
                        />
                      </View>
                      <Button
                        label="Buscar"
                        variant="secondary"
                        disabled={busy}
                        onPress={() => {
                          setPage(1);
                          setQuery(search);
                        }}
                      />
                    </View>
                  ) : null}
                  {!loadFailed ? (
                    <Text style={s.small}>
                      {total} {total === 1 ? "registro" : "registros"}
                    </Text>
                  ) : (
                    <Text style={s.muted}>
                      No se pudo consultar este módulo. Revisa el mensaje anterior o vuelve a
                      abrirlo después de corregir la configuración.
                    </Text>
                  )}
                  {!loadFailed && !loading && !items.length ? (
                    <View style={s.empty}>
                      <Text style={s.sectionTitle}>Aún no hay registros</Text>
                      <Text style={s.muted}>Agrega el primer registro o ajusta tu búsqueda.</Text>
                    </View>
                  ) : (
                    items.map((item) => (
                      <View
                        key={item._id}
                        style={[
                          s.record,
                          compact && { flexDirection: "column", alignItems: "stretch" },
                        ]}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={s.recordTitle}>
                            {item.fullName ?? item.name ?? item.displayName}
                          </Text>
                          {item.email ? <Text style={s.muted}>{item.email}</Text> : null}
                          {item.sku ? (
                            <Text style={s.muted}>
                              SKU: {item.sku} · {item.category}
                            </Text>
                          ) : null}
                          {item.stock !== undefined && item.price !== undefined ? (
                            <Text style={s.small}>
                              Stock: {item.stock} · Precio: ${item.price.toFixed(2)}
                            </Text>
                          ) : null}
                          {item.permissions ? (
                            <Text style={s.small}>
                              {item.permissions.length} permisos ·{" "}
                              {item.protected ? "Rol protegido" : "Rol personalizado"}
                            </Text>
                          ) : null}
                          {item.enabledModules ? (
                            <Text style={s.muted}>
                              {item.enabledModules
                                .map((value) => moduleNames[value] ?? value)
                                .join(" · ") || "Sin módulos habilitados"}
                            </Text>
                          ) : null}
                          {item.active !== undefined ? (
                            <Text style={[s.small, { color: item.active ? "#247454" : "#7C4350" }]}>
                              {item.active ? "● Activo" : "○ Inactivo"}
                            </Text>
                          ) : null}
                        </View>
                        {!item.protected &&
                        (can(`${module}.update`) ||
                          (module === "users" && can("users.assignRoles"))) ? (
                          <Button
                            label="Editar"
                            variant="secondary"
                            disabled={busy}
                            onPress={() => edit(item)}
                          />
                        ) : null}
                      </View>
                    ))
                  )}
                  <View style={s.pagination}>
                    <Button
                      label="← Anterior"
                      variant="secondary"
                      disabled={page <= 1 || loading || busy}
                      onPress={() => setPage(page - 1)}
                    />
                    <Text style={s.small}>
                      Página {page} de {totalPages}
                    </Text>
                    <Button
                      label="Siguiente →"
                      variant="secondary"
                      disabled={page >= totalPages || loading || busy}
                      onPress={() => setPage(page + 1)}
                    />
                  </View>
                </View>
              </>
            )}
            <Text style={s.footer}>
              ERP CORE · {company?.name} · Información de tu empresa activa
            </Text>
          </ScrollView>
        </View>
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  outer: { flex: 1, padding: 28, backgroundColor: "transparent" },
  shell: {
    flex: 1,
    width: "100%",
    maxWidth: 1500,
    alignSelf: "center",
    borderRadius: coreTokens.radius.shell,
    backgroundColor: coreTokens.colors.background,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#FFFFFF",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    padding: 24,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderColor: coreTokens.colors.border,
    flexWrap: "wrap",
  },
  brand: { fontSize: 22, fontWeight: "800", color: coreTokens.colors.text },
  brandLight: { fontSize: 15, fontWeight: "500", color: coreTokens.colors.muted },
  companyButton: {
    padding: 12,
    borderRadius: coreTokens.radius.control,
    backgroundColor: "#F6F8FB",
    minHeight: 44,
    maxWidth: 240,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 16,
    backgroundColor: "#E2EEFE",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { color: "#246AB0", fontWeight: "700", fontSize: 19 },
  layout: { flex: 1, flexDirection: "row" },
  nav: {
    width: 236,
    padding: 20,
    gap: 8,
    backgroundColor: "#FFF",
    borderRightWidth: 1,
    borderColor: coreTokens.colors.border,
  },
  navHeading: {
    color: coreTokens.colors.muted,
    fontSize: 10,
    letterSpacing: 1.5,
    fontWeight: "700",
    marginBottom: 14,
    paddingLeft: 10,
  },
  navItem: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
    padding: 13,
    borderRadius: coreTokens.radius.control,
    minHeight: 48,
  },
  navActive: { backgroundColor: coreTokens.colors.selected },
  navIcon: { fontSize: 24, color: "#63718A" },
  navLabel: { fontSize: coreTokens.fontSize.body, fontWeight: "500", color: "#516178" },
  blue: { color: "#176BB0" },
  navFooter: { marginTop: 48, backgroundColor: "#F4F8FF", padding: 16, borderRadius: 18, gap: 10 },
  badge: { borderRadius: 8, backgroundColor: "#E3EEFE", padding: 8 },
  badgeText: { fontSize: 10, color: "#276AA9", fontWeight: "600" },
  main: { flex: 1 },
  mainContent: { padding: 32, gap: 24 },
  breadcrumb: { flexDirection: "row", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  titleRow: { flexDirection: "row", gap: 16, alignItems: "center", flexWrap: "wrap" },
  title: {
    fontSize: coreTokens.fontSize.title,
    fontWeight: "700",
    color: coreTokens.colors.text,
    marginBottom: 8,
  },
  body: { fontSize: coreTokens.fontSize.body, lineHeight: 22, color: "#34445B" },
  muted: { fontSize: coreTokens.fontSize.body, lineHeight: 22, color: coreTokens.colors.muted },
  small: { fontSize: coreTokens.fontSize.caption, lineHeight: 19, color: coreTokens.colors.muted },
  label: { fontSize: coreTokens.fontSize.body, fontWeight: "600", color: "#27364D" },
  welcome: {
    flexDirection: "row",
    alignItems: "center",
    padding: 28,
    borderRadius: 22,
    backgroundColor: "#EAF3FE",
    gap: 16,
  },
  eyebrow: {
    fontSize: 10,
    letterSpacing: 1.4,
    fontWeight: "700",
    color: "#3578B5",
    marginBottom: 14,
  },
  welcomeTitle: {
    fontSize: 29,
    lineHeight: 37,
    fontWeight: "700",
    color: "#193859",
    marginBottom: 12,
  },
  welcomeIcon: { fontSize: 100, color: "#8ABCE7" },
  stats: { flexDirection: "row", flexWrap: "wrap", gap: 18 },
  statCard: {
    flex: 1,
    minWidth: 170,
    backgroundColor: "#FFF",
    padding: 22,
    borderRadius: coreTokens.radius.card,
    borderWidth: 1,
    borderColor: "#E8EDF5",
    gap: 8,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  statValue: { fontSize: 34, fontWeight: "700", color: coreTokens.colors.text },
  statLink: { color: "#2373B5", fontSize: coreTokens.fontSize.caption, marginTop: 12 },
  panel: {
    padding: 24,
    borderRadius: coreTokens.radius.card,
    backgroundColor: "#FFF",
    borderWidth: 1,
    borderColor: "#E8EDF5",
    gap: 18,
  },
  sectionTitle: { fontSize: 19, fontWeight: "700", color: coreTokens.colors.text },
  quickLinks: { flexDirection: "row", gap: 12, flexWrap: "wrap" },
  activityRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderColor: "#F0F3F7",
  },
  activityDot: { color: "#339AF0", fontSize: 13 },
  field: { gap: 8 },
  input: {
    borderWidth: 1,
    borderColor: "#DCE4EE",
    backgroundColor: "#FBFCFE",
    color: coreTokens.colors.text,
    borderRadius: 10,
    padding: 12,
    minHeight: 46,
    fontSize: 15,
  },
  searchInput: {
    backgroundColor: "#F5F7FA",
    borderRadius: 10,
    padding: 12,
    color: coreTokens.colors.text,
    fontSize: 13,
    minHeight: 44,
  },
  searchRow: { flexDirection: "row", gap: 12, alignItems: "center" },
  actions: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 12 },
  toggle: { padding: 12, borderWidth: 1, borderColor: "#E2E8F0", borderRadius: 10, minHeight: 44 },
  toggleSelected: { borderColor: "#79AFE0", backgroundColor: "#EFF7FE" },
  record: {
    paddingVertical: 18,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    borderBottomWidth: 1,
    borderColor: "#EEF2F7",
  },
  recordTitle: { fontSize: 16, fontWeight: "600", color: coreTokens.colors.text, marginBottom: 5 },
  pagination: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  empty: { padding: 28, alignItems: "center", gap: 10 },
  loading: { padding: 16, flexDirection: "row", gap: 12 },
  loginOuter: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    gap: 18,
    minHeight: "100%",
  },
  loginCard: {
    backgroundColor: "#FFF",
    width: "100%",
    maxWidth: 490,
    borderRadius: 28,
    padding: 32,
    gap: 20,
  },
  loginTitle: { fontSize: 31, fontWeight: "700", color: coreTokens.colors.text, lineHeight: 40 },
  error: {
    backgroundColor: "#FFF0F0",
    padding: 14,
    borderRadius: coreTokens.radius.control,
    borderWidth: 1,
    borderColor: "#F5C8C8",
  },
  errorText: { color: "#9C2531", fontSize: coreTokens.fontSize.body, lineHeight: 21 },
  success: { backgroundColor: "#EAF7EF", padding: 14, borderRadius: coreTokens.radius.control },
  userMenu: {
    alignSelf: "flex-end",
    padding: 20,
    backgroundColor: "#FFF",
    gap: 12,
    borderBottomWidth: 1,
    borderColor: "#E2E8F0",
  },
  footer: {
    textAlign: "center",
    color: coreTokens.colors.muted,
    fontSize: 11,
    paddingVertical: 16,
  },
});
